import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    topic: 'venture',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ENDPOINT = 'https://script.google.com/macros/s/AKfycbyuY_mgZTPTuKts60v4etmVGf-dpClZA6tj-HyEVzZi2UwPniyJVH3Nmqh5pSXfCZvO1w/exec';
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = new FormData();
      payload.append('name', formData.name);
      payload.append('email', formData.email);
      payload.append('company', formData.company);
      payload.append('topic', formData.topic);
      payload.append('message', formData.message);
      payload.append('submittedAt', new Date().toISOString());
      // Google Apps Script doesn't return CORS headers, so we use no-cors.
      // We can't read the response, but the request will reach the script.
      await fetch(ENDPOINT, {
        method: 'POST',
        mode: 'no-cors',
        body: payload
      });
      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        company: '',
        topic: 'Pitch a venture idea',
        message: ''
      });
    } catch (err) {
      setError('Something went wrong sending your message. Please try again, or email us directly at hello@launchcave.com.');
    } finally {
      setSubmitting(false);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const topics = [{
    value: 'Pitch a venture idea',
    label: 'Pitch a venture idea'
  }, {
    value: 'Partnership inquiry',
    label: 'Partnership inquiry'
  }, {
    value: 'Careers & talent',
    label: 'Careers & talent'
  }, {
    value: 'Press & media',
    label: 'Press & media'
  }, {
    value: 'Something else',
    label: 'Something else'
  }];
  return <section id="contact" className="py-32 px-6 md:px-12 bg-cream relative overflow-hidden" data-id="element-34">
      {/* Decorative background */}
      <div className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-orange/5 rounded-full blur-3xl pointer-events-none" data-id="element-35" />

      <div className="max-w-7xl mx-auto relative" data-id="element-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20" data-id="element-37">
          {/* Left column */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6
        }} className="lg:col-span-5 lg:sticky lg:top-32 self-start" data-id="element-38">
            <h2 className="text-sm font-sans font-semibold tracking-widest uppercase text-orange mb-6" data-id="element-39">
              Get in touch
            </h2>
            <h3 className="text-4xl md:text-6xl font-serif text-navy leading-[1.05] text-balance mb-8" data-id="element-40">
              Let's build <span className="text-orange italic" data-id="element-41">something</span>{' '}
              together.
            </h3>
            <p className="text-lg text-navy/70 font-light leading-relaxed mb-12 max-w-md" data-id="element-42">
              Whether you're an operator with an idea, a potential partner, or
              just curious — drop us a line. We read everything.
            </p>

            <div className="space-y-6 pt-8 border-t border-navy/10" data-id="element-43">
              <div data-id="element-44">
              {/*
                <p className="text-xs font-semibold tracking-wider uppercase text-navy/50 mb-2">
                  Email
                </p>
                
                <a
                  href="mailto:hello@launchcave.com"
                  className="text-lg text-navy hover:text-orange transition-colors"
                >
                  hello@launchcave.com
                </a>
                */}
              </div>
              <div data-id="element-45">
                <p className="text-xs font-semibold tracking-wider uppercase text-navy/50 mb-2" data-id="element-46">
                  Studio
                </p>
                <p className="text-lg text-navy/80 font-light" data-id="element-47">
                  Remote-first, globally distributed
                </p>
              </div>
            </div>
          </motion.div>

          {/* Right column — form */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6,
          delay: 0.15
        }} className="lg:col-span-7" data-id="element-48">
            {submitted ? <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="bg-white rounded-2xl border border-navy/5 p-12 md:p-16 text-center" data-id="element-49">
                <div className="w-16 h-16 rounded-full bg-orange/10 flex items-center justify-center mx-auto mb-6" data-id="element-50">
                  <Check className="w-8 h-8 text-orange" data-id="element-51" />
                </div>
                <h4 className="text-3xl font-serif text-navy mb-4" data-id="element-52">
                  Message received.
                </h4>
                <p className="text-navy/70 font-light text-lg max-w-md mx-auto" data-id="element-53">
                  Thanks for reaching out. We'll get back to you within a few
                  business days.
                </p>
              </motion.div> : <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-navy/5 p-8 md:p-12 space-y-6" data-id="element-54">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-55">
                  <Field label="Name" name="name" type="text" placeholder="Jane Doe" value={formData.name} onChange={handleChange} required data-id="element-56" />
                  <Field label="Email" name="email" type="email" placeholder="jane@example.com" value={formData.email} onChange={handleChange} required data-id="element-57" />
                </div>

                <Field label="Company / Project" name="company" type="text" placeholder="Optional" value={formData.company} onChange={handleChange} data-id="element-58" />

                <div data-id="element-59">
                  <label htmlFor="topic" className="block text-xs font-semibold tracking-wider uppercase text-navy/60 mb-2" data-id="element-60">
                    What's this about?
                  </label>
                  <select id="topic" name="topic" value={formData.topic} onChange={handleChange} className="w-full bg-cream/50 border border-navy/10 rounded-lg px-4 py-3 text-navy focus:outline-none focus:border-orange focus:ring-2 focus:ring-orange/20 transition-all font-light" data-id="element-61">
                    {topics.map(t => <option key={t.value} value={t.value} data-id="element-62">
                        {t.label}
                      </option>)}
                  </select>
                </div>

                <div data-id="element-63">
                  <label htmlFor="message" className="block text-xs font-semibold tracking-wider uppercase text-navy/60 mb-2" data-id="element-64">
                    Message
                  </label>
                  <textarea id="message" name="message" rows={5} placeholder="Tell us what you're thinking about..." value={formData.message} onChange={handleChange} required className="w-full bg-cream/50 border border-navy/10 rounded-lg px-4 py-3 text-navy placeholder-navy/30 focus:outline-none focus:border-orange focus:ring-2 focus:ring-orange/20 transition-all font-light resize-none" data-id="element-65" />
                </div>

                {error && <div className="px-4 py-3 rounded-lg bg-orange/10 border border-orange/30 text-sm text-navy" data-id="element-66">
                    {error}
                  </div>}

                <div className="pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-67">
                  <p className="text-xs text-navy/50 font-light" data-id="element-68">
                    {/*We typically reply within 2 business days.*/}
                  </p>
                  <button type="submit" disabled={submitting} className="group inline-flex items-center justify-center gap-2 bg-navy text-cream px-6 py-3.5 rounded-full text-sm font-medium hover:bg-orange transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-navy" data-id="element-69">
                    {submitting ? 'Sending...' : 'Send message'}
                    {!submitting && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-70" />}
                  </button>
                </div>
              </form>}
          </motion.div>
        </div>
      </div>
    </section>;
}
type FieldProps = {
  label: string;
  name: string;
  type: string;
  placeholder?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
};
function Field({
  label,
  name,
  type,
  placeholder,
  value,
  onChange,
  required
}: FieldProps) {
  return <div data-id="element-71">
      <label htmlFor={name} className="block text-xs font-semibold tracking-wider uppercase text-navy/60 mb-2" data-id="element-72">
        {label}
        {required && <span className="text-orange ml-1" data-id="element-73">*</span>}
      </label>
      <input id={name} name={name} type={type} placeholder={placeholder} value={value} onChange={onChange} required={required} className="w-full bg-cream/50 border border-navy/10 rounded-lg px-4 py-3 text-navy placeholder-navy/30 focus:outline-none focus:border-orange focus:ring-2 focus:ring-orange/20 transition-all font-light" data-id="element-74" />
    </div>;
}